/**
 * "Add layers" dialog.
 *
 * One popup with two entry points, matching how the user picks data:
 *   • From server — the existing GeoServer WMS / ArcGIS REST discovery flow,
 *     moved here unchanged. The parent still owns the URLs, the loaders, and
 *     the add/remove handlers; this component only presents them.
 *   • From this computer — parses a GeoJSON/CSV locally and hands back a
 *     ready-to-add layer.
 *
 * Nothing about service discovery changed: the same `loadGeoServerLayers`,
 * `addGeoServerLayer`, `addAllGeoServerLayers`, and ArcGIS equivalents run.
 *
 * WHY ADDING IS ASYNCHRONOUS NOW
 * A zipped File Geodatabase is read by the API rather than in the tab, and its
 * feature classes are converted one at a time as they are picked. So the file
 * step produces a LIST of layers that may not carry their features yet, and
 * "Add to map" is what fetches the ones that were ticked. Every other format
 * still resolves instantly, and none of them notice the difference.
 */

import { useRef, useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  FileUp,
  Folder,
  Globe,
  Image as ImageIcon,
  Layers as LayersIcon,
  Link2,
  Loader2,
  Check,
  Library,
  X,
} from 'lucide-react';
import type { Layer, LayerGeometryType, ServerConnection } from '../../types';
import ExistingLayerPicker from './ExistingLayerPicker';
import type { ServerConnectionBrowseResult, ServerConnectionService } from '../../services/serverConnectionsService';
import {
  LocalFileError,
  MAX_GDB_FILE_BYTES,
  MAX_LOCAL_FILE_BYTES,
  MAX_RASTER_FILE_BYTES,
  parseLocalFile,
  resolveLocalLayers,
  type ParsedImport,
  type ParsedLocalLayer,
} from '../../layers/localFile';

export type AddLayersMode = 'server' | 'local' | 'existing';

/** True once a URL carries `user:pass@` userinfo — the shape a resolved,
 *  saved-connection URL takes (see serverConnectionsService.resolve). Used
 *  to keep a connected URL's credentials out of the plain, editable text box
 *  below: once connected, that box turns into a read-only summary instead. */
function hasEmbeddedCredentials(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:\/\/[^/?#@]+@/i.test(url.trim());
}

export interface ServiceLayerOption {
  key: string;
  title: string;
  added: boolean;
  onAdd: () => void;
  onRemove: () => void;
}

interface Props {
  open: boolean;
  mode: AddLayersMode;
  onModeChange: (mode: AddLayersMode) => void;
  onClose: () => void;

  /* ── Server side (owned by the Builder) ── */
  serviceType: 'geoserver' | 'arcgis';
  onServiceTypeChange: (type: 'geoserver' | 'arcgis') => void;
  geoServerUrl: string;
  onGeoServerUrlChange: (url: string) => void;
  onLoadGeoServer: () => void;
  geoServerLoading: boolean;
  geoServerError: string | null;
  arcgisUrl: string;
  onArcgisUrlChange: (url: string) => void;
  onLoadArcgis: () => void;
  arcgisLoading: boolean;
  arcgisError: string | null;
  /** Discovered layers for whichever service is active. */
  options: ServiceLayerOption[];
  onAddAll?: () => void;
  pendingCount: number;

  /* ── Saved connections (Settings › ArcGIS Servers / GeoServer) ── */
  /** All saved connections, of both types — filtered to `serviceType` here. */
  connections: ServerConnection[];
  connectionsLoading: boolean;
  selectedConnectionId: string | null;
  onSelectConnection: (id: string | null) => void;
  /**
   * GeoServer: resolves the selected connection's credentials and loads its
   * layers directly — the connected equivalent of `onLoadGeoServer`.
   * ArcGIS: starts (or restarts) browsing the selected connection's services
   * from its root — see `connectionBrowse`.
   */
  onConnectSelected: () => void;
  connecting: boolean;
  connectError: string | null;
  /** ArcGIS only. One folder's worth of the connection's services; null
   *  before "Connect" is pressed or after picking a different connection. */
  connectionBrowse: ServerConnectionBrowseResult | null;
  connectionBrowseLoading: boolean;
  connectionBrowseError: string | null;
  /** Browses into a folder, or back to the root for `path: ''`. */
  onBrowseConnection: (path: string) => void;
  /** Resolves and loads the picked service's layers — the ArcGIS equivalent
   *  of `onConnectSelected` finishing, one level down. */
  onSelectConnectionService: (service: ServerConnectionService) => void;

  /* ── Local side ── */
  /** One file can contain several layers (KML folders), so this takes a list. */
  onAddLocalLayer: (layers: ParsedLocalLayer[]) => void;
  /** The map's current view — where a plain PNG/JPG (no georeferencing of
   *  its own) is placed by default. See `localFile.ts`'s `fromPlainImage`. */
  mapCenter: [number, number];
  mapZoom: number;

  /* ── Existing layer ── */
  /** The module currently open in the Builder — excluded from "My layers",
   *  since reusing a layer already in this same map is what Duplicate is for. */
  currentModuleId: string;
  currentUserId: string | undefined;
  onAddExistingLayer: (layer: Layer) => void;
}

const GEOMETRY_LABEL: Record<LayerGeometryType, string> = {
  point: 'Point',
  line: 'Line',
  polygon: 'Polygon',
  unknown: 'Unknown',
};

export default function AddLayersDialog({
  open,
  mode,
  onModeChange,
  onClose,
  serviceType,
  onServiceTypeChange,
  geoServerUrl,
  onGeoServerUrlChange,
  onLoadGeoServer,
  geoServerLoading,
  geoServerError,
  arcgisUrl,
  onArcgisUrlChange,
  onLoadArcgis,
  arcgisLoading,
  arcgisError,
  options,
  onAddAll,
  pendingCount,
  connections,
  connectionsLoading,
  selectedConnectionId,
  onSelectConnection,
  onConnectSelected,
  connecting,
  connectError,
  connectionBrowse,
  connectionBrowseLoading,
  connectionBrowseError,
  onBrowseConnection,
  onSelectConnectionService,
  onAddLocalLayer,
  mapCenter,
  mapZoom,
  currentModuleId,
  currentUserId,
  onAddExistingLayer,
}: Props) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  /** "Manual URL" vs "Saved connection" — a sub-choice within `mode==='server'`,
   *  local because it is pure presentation: whichever one is picked just ends
   *  up driving the same `arcgisUrl`/`geoServerUrl` state either way. */
  const [urlSource, setUrlSource] = useState<'manual' | 'connection'>('manual');
  const [parsing, setParsing] = useState(false);
  /**
   * What the spinner should say. A zipped geodatabase is uploaded to the API
   * and can take a minute on a slow line; "Reading file…" for that long looks
   * like the app has hung, when in fact it is doing exactly what it should.
   */
  const [parsingLabel, setParsingLabel] = useState('Reading file…');
  const [imported, setImported] = useState<ParsedImport | null>(null);
  /** Which of the discovered layers to add; all usable ones are ticked. */
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [localError, setLocalError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /** Non-null while the chosen layers are being fetched, as "done of total". */
  const [adding, setAdding] = useState<{ done: number; total: number } | null>(null);

  if (!open) return null;

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    // Whatever was open before is finished with the moment a new file is
    // picked — otherwise the server holds the previous geodatabase for its
    // whole TTL for nothing.
    imported?.release?.();

    setParsing(true);
    setParsingLabel(
      /\.zip$/i.test(file.name) && file.size > MAX_LOCAL_FILE_BYTES
        ? 'Uploading geodatabase…'
        : 'Reading file…',
    );
    setLocalError(null);
    setImported(null);
    try {
      const result = await parseLocalFile(file, mapCenter, mapZoom);
      setImported(result);
      // Layers the server already said cannot be added start unticked, so
      // "Add all" never means "add all except the ones that will fail".
      setSelected(
        new Set(
          result.layers.flatMap((layer, index) => (layer.unavailable ? [] : [index])),
        ),
      );
    } catch (error) {
      setLocalError(
        error instanceof LocalFileError
          ? error.message
          : 'This file could not be read. Use GeoJSON or a CSV with longitude/latitude columns.',
      );
    } finally {
      setParsing(false);
    }
  };

  /**
   * Loads the ticked layers, then hands them over.
   *
   * For everything parsed in the browser this resolves instantly — the
   * features were already there. For a geodatabase it is where the chosen
   * feature classes are actually converted, one at a time, which is what the
   * counter reports.
   */
  const addSelected = async () => {
    if (!imported || selected.size === 0) return;
    const chosen = imported.layers.filter((_, index) => selected.has(index));

    setAdding({ done: 0, total: chosen.length });
    setLocalError(null);
    try {
      const layers = await resolveLocalLayers(chosen, (done, total) =>
        setAdding({ done, total }),
      );
      onAddLocalLayer(layers);
      imported.release?.();
      reset();
      onClose();
    } catch (error) {
      // The import stays on screen so the other layers can still be added.
      setLocalError(
        error instanceof Error ? error.message : 'Those layers could not be loaded.',
      );
    } finally {
      setAdding(null);
    }
  };

  const reset = () => {
    setImported(null);
    setSelected(new Set());
    setLocalError(null);
    setAdding(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  /** Closing abandons the import, so the server should stop holding it. */
  const closeAndRelease = () => {
    imported?.release?.();
    reset();
    onClose();
  };

  const loading = serviceType === 'geoserver' ? geoServerLoading : arcgisLoading;
  const error = serviceType === 'geoserver' ? geoServerError : arcgisError;

  return (
    <div
      className="gl-modal-backdrop fixed inset-0 z-[9000] flex items-center justify-center px-4"
      onClick={closeAndRelease}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add layers"
        className="gl-modal gl-surface--strong flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="gl-header flex flex-shrink-0 items-center justify-between px-5 py-3.5">
          <h2 className="text-sm font-semibold text-slate-900">Add layers</h2>
          <button
            type="button"
            onClick={closeAndRelease}
            aria-label="Close"
            className="gl-btn gl-btn--ghost p-1.5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Source switch */}
        <div className="flex flex-shrink-0 gap-1 px-5 pt-3">
          {([
            { id: 'server' as AddLayersMode, label: 'From server', icon: Globe },
            { id: 'local' as AddLayersMode, label: 'From this computer', icon: FileUp },
            { id: 'existing' as AddLayersMode, label: 'Existing layer', icon: Library },
          ]).map((entry) => {
            const Icon = entry.icon;
            const active = mode === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => onModeChange(entry.id)}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
                  active ? 'text-blue-600' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {entry.label}
                {active && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
              </button>
            );
          })}
        </div>
        <div className="gl-divider mx-5" />

        {/* ── Body ────────────────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {mode === 'server' ? (
            <div className="space-y-3">
              <div className="grid max-w-xs grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1">
                {([
                  { id: 'geoserver' as const, label: 'GeoServer' },
                  { id: 'arcgis' as const, label: 'ArcGIS REST' },
                ]).map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => onServiceTypeChange(entry.id)}
                    className={`rounded-md px-2 py-1.5 text-xs font-medium transition ${
                      serviceType === entry.id
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              {/* Manual URL vs a connection saved in Settings › ArcGIS
                  Servers / GeoServer — a sub-choice of "From server", not a
                  separate top-level tab, so it shares the one results list
                  below whichever way the layers were discovered. */}
              <div className="flex gap-1 text-xs">
                {([
                  { id: 'manual' as const, label: 'Manual URL' },
                  { id: 'connection' as const, label: 'Saved connection' },
                ]).map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setUrlSource(entry.id)}
                    className={`rounded-full px-2.5 py-1 font-medium transition ${
                      urlSource === entry.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              {urlSource === 'manual' ? (
                <div className="flex flex-col gap-2 sm:flex-row">
                  {(() => {
                    const currentUrl = serviceType === 'geoserver' ? geoServerUrl : arcgisUrl;
                    const onCurrentUrlChange = serviceType === 'geoserver' ? onGeoServerUrlChange : onArcgisUrlChange;
                    // A URL just resolved from a saved connection carries its
                    // credentials — shown as a read-only summary rather than
                    // a plain editable box, so switching here after using
                    // "Saved connection" never puts a password on screen.
                    return hasEmbeddedCredentials(currentUrl) ? (
                      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-800">
                        <Link2 className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="min-w-0 flex-1 truncate">
                          Using a saved connection's credentials
                        </span>
                        <button
                          type="button"
                          onClick={() => onCurrentUrlChange('')}
                          className="flex-shrink-0 font-semibold text-teal-700 underline-offset-2 hover:underline"
                        >
                          Clear
                        </button>
                      </div>
                    ) : (
                      <input
                        value={currentUrl}
                        onChange={(event) => onCurrentUrlChange(event.target.value)}
                        onKeyDown={(event) =>
                          event.key === 'Enter' && (serviceType === 'geoserver' ? onLoadGeoServer() : onLoadArcgis())
                        }
                        placeholder={
                          serviceType === 'geoserver'
                            ? 'GeoServer WMS URL'
                            : 'https://host/arcgis/rest/services/<service>/MapServer'
                        }
                        aria-label={serviceType === 'geoserver' ? 'GeoServer WMS URL' : 'ArcGIS REST service URL'}
                        className="gl-input min-w-0 flex-1 px-3 py-2 text-sm"
                      />
                    );
                  })()}
                  <button
                    type="button"
                    onClick={serviceType === 'geoserver' ? onLoadGeoServer : onLoadArcgis}
                    disabled={loading}
                    className="gl-btn gl-btn--primary px-4 py-2 text-sm"
                  >
                    {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {loading ? 'Loading…' : 'Load layers'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {connectionsLoading ? (
                    <p className="text-xs text-slate-400">Loading saved connections…</p>
                  ) : connections.filter((c) => c.type === serviceType).length === 0 ? (
                    <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
                      No saved {serviceType === 'geoserver' ? 'GeoServer' : 'ArcGIS Server'} connections yet.
                      Add one in Settings › {serviceType === 'geoserver' ? 'GeoServer' : 'ArcGIS Servers'}.
                    </p>
                  ) : !connectionBrowse ? (
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <select
                        value={selectedConnectionId ?? ''}
                        onChange={(event) => onSelectConnection(event.target.value || null)}
                        aria-label="Saved connection"
                        className="gl-input min-w-0 flex-1 px-3 py-2 text-sm"
                      >
                        <option value="">Choose a connection…</option>
                        {connections
                          .filter((c) => c.type === serviceType)
                          .map((connection) => (
                            <option key={connection.id} value={connection.id}>
                              {connection.name} — {connection.baseUrl}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={onConnectSelected}
                        disabled={!selectedConnectionId || connecting}
                        className="gl-btn gl-btn--primary px-4 py-2 text-sm"
                      >
                        {connecting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {connecting
                          ? 'Connecting…'
                          : serviceType === 'geoserver'
                            ? 'Connect & load layers'
                            : 'Connect'}
                      </button>
                    </div>
                  ) : (
                    /* ArcGIS only: browsing this connection's services — a
                       folder/service picker, since one connection may serve
                       many MapServer/FeatureServer services rather than one. */
                    <div className="rounded-lg border border-slate-200">
                      <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-2 text-[11px] text-slate-500">
                        <button
                          type="button"
                          onClick={() => onBrowseConnection('')}
                          className="font-medium text-slate-600 hover:text-blue-600"
                        >
                          Root
                        </button>
                        {connectionBrowse.path
                          .split('/')
                          .filter(Boolean)
                          .map((segment, index, segments) => (
                            <span key={index} className="flex items-center gap-1">
                              <ChevronRight className="h-3 w-3 text-slate-300" />
                              <button
                                type="button"
                                onClick={() => onBrowseConnection(segments.slice(0, index + 1).join('/'))}
                                className="font-medium text-slate-600 hover:text-blue-600"
                              >
                                {segment}
                              </button>
                            </span>
                          ))}
                        <button
                          type="button"
                          onClick={() => onSelectConnection(null)}
                          className="ml-auto flex-shrink-0 text-slate-400 hover:text-slate-600"
                        >
                          Change connection
                        </button>
                      </div>

                      {connectionBrowseLoading ? (
                        <p className="px-3 py-6 text-center text-xs text-slate-400">Loading…</p>
                      ) : connectionBrowse.folders.length === 0 && connectionBrowse.services.length === 0 ? (
                        <p className="px-3 py-6 text-center text-xs text-slate-400">Nothing here.</p>
                      ) : (
                        <ul className="max-h-56 overflow-y-auto">
                          {connectionBrowse.folders.map((folder) => (
                            <li key={folder} className="border-b border-slate-50 last:border-0">
                              <button
                                type="button"
                                onClick={() =>
                                  onBrowseConnection(
                                    connectionBrowse.path ? `${connectionBrowse.path}/${folder}` : folder,
                                  )
                                }
                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                              >
                                <Folder className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                                <span className="min-w-0 flex-1 truncate">{folder}</span>
                              </button>
                            </li>
                          ))}
                          {connectionBrowse.services.map((service) => (
                            <li key={`${service.name}-${service.type}`} className="border-b border-slate-50 last:border-0">
                              <button
                                type="button"
                                onClick={() => onSelectConnectionService(service)}
                                disabled={connecting}
                                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-blue-50 disabled:opacity-50"
                              >
                                <span className="flex min-w-0 items-center gap-2">
                                  <LayersIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-400" />
                                  <span className="min-w-0 truncate">
                                    {service.name || 'This service'}
                                  </span>
                                </span>
                                <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                                  {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : service.type}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {(connectError || connectionBrowseError) && (
                    <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                      {connectError ?? connectionBrowseError}
                    </p>
                  )}
                </div>
              )}

              {error && (
                <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}

              {options.length > 0 && (
                <div className="rounded-lg border border-slate-200">
                  <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Available layers ({options.length})
                    </span>
                    {onAddAll && (
                      <button
                        type="button"
                        onClick={onAddAll}
                        disabled={pendingCount === 0}
                        className="gl-btn gl-btn--accent px-2.5 py-1 text-xs"
                      >
                        {pendingCount === 0 ? 'All added' : `Add all (${pendingCount})`}
                      </button>
                    )}
                  </div>
                  <ul className="max-h-72 overflow-y-auto">
                    {options.map((option) => (
                      <li
                        key={option.key}
                        className="flex items-center justify-between gap-2 border-b border-slate-50 px-3 py-2"
                      >
                        <span className="min-w-0 truncate text-xs text-slate-700">
                          {option.title}
                        </span>
                        {option.added ? (
                          <button
                            type="button"
                            onClick={option.onRemove}
                            className="gl-btn gl-btn--danger flex-shrink-0 px-2 py-1 text-xs"
                          >
                            Remove
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={option.onAdd}
                            className="gl-btn gl-btn--ghost flex-shrink-0 px-2 py-1 text-xs text-blue-600"
                          >
                            Add
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {options.length === 0 && !loading && !error && !(urlSource === 'connection' && connectionBrowse) && (
                <p className="rounded-lg bg-slate-50 px-3 py-6 text-center text-xs text-slate-400">
                  {urlSource === 'manual' ? (
                    <>Enter a service URL and choose <strong>Load layers</strong>.</>
                  ) : serviceType === 'geoserver' ? (
                    <>Choose a saved connection and select <strong>Connect &amp; load layers</strong>.</>
                  ) : (
                    <>Choose a saved connection and select <strong>Connect</strong>, then pick a service.</>
                  )}
                </p>
              )}
            </div>
          ) : mode === 'local' ? (
            /* ── Local file ───────────────────────────────────────────── */
            <div className="space-y-3">
              <div
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  void handleFile(event.dataTransfer.files?.[0]);
                }}
                className={`rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
                  dragging ? 'border-blue-400 bg-blue-50/60' : 'border-slate-300'
                }`}
              >
                <FileUp className="mx-auto h-7 w-7 text-slate-300" />
                <p className="mt-2 text-sm font-medium text-slate-700">
                  Drop a file here, or
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="ml-1 font-semibold text-blue-600 underline-offset-2 hover:underline"
                  >
                    browse
                  </button>
                </p>
                <p className="mt-1 text-[11px] text-slate-400">
                  GeoJSON · KML · KMZ · Shapefile (.zip) · CSV/TSV with lon &amp; lat columns
                  <br />up to {MAX_LOCAL_FILE_BYTES / 1024 / 1024} MB
                </p>
                {/* Called out separately: it is an image, not features, so
                    none of the vector limits above describe it. */}
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  <strong className="font-semibold text-slate-600">GeoTIFF (.tif/.tiff)</strong>{' '}
                  — a georeferenced raster image, up to {MAX_RASTER_FILE_BYTES / 1024 / 1024} MB.
                  Compressed automatically before it is added, so the module stays light — no
                  attributes, pop-up, or styling; only opacity and the visibility range every
                  layer has.
                </p>
                {/* A plain image has none of a GeoTIFF's embedded extent, so
                    it is called out separately rather than folded into that
                    paragraph — someone picking a photo should know up front
                    where it will land, not discover it after adding it. */}
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  <strong className="font-semibold text-slate-600">PNG / JPG</strong> — a plain
                  image with no georeferencing, placed at the centre of the current map view (pan
                  and zoom there first). Same {MAX_RASTER_FILE_BYTES / 1024 / 1024} MB limit and
                  automatic compression as a GeoTIFF.
                </p>
                {/* Called out separately because it behaves differently in two
                    ways the user should know before they drop the file: it is
                    a folder they must zip, and it is read on the server. */}
                <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                  <strong className="font-semibold text-slate-600">
                    File Geodatabase · GeoPackage
                  </strong>{' '}
                  — zip the <code className="text-slate-600">.gdb</code> folder and add the
                  .zip, up to {MAX_GDB_FILE_BYTES / 1024 / 1024} MB. Each feature class
                  becomes its own layer.
                  <br />
                  <span className="text-slate-400">
                    These are read by the BlueView server, not in your browser, and the
                    uploaded copy is deleted shortly afterwards.
                  </span>
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".geojson,.json,.kml,.kmz,.zip,.shp,.csv,.tsv,.txt,.tif,.tiff,.png,.jpg,.jpeg"
                  className="hidden"
                  onChange={(event) => void handleFile(event.target.files?.[0])}
                />
              </div>

              {parsing && (
                <p className="flex items-center gap-2 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {parsingLabel}
                </p>
              )}

              {localError && (
                <div role="alert" className="rounded-lg bg-red-50 px-3 py-2.5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    Could not import this file
                  </p>
                  <p className="mt-1 text-[11px] leading-relaxed text-red-600">{localError}</p>
                </div>
              )}

              {imported && (
                <div className="space-y-2">
                  {/* Pre-import preview: what the file actually contains, and
                      which of it to bring in. */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-slate-700">
                      {imported.layers.length === 1
                        ? '1 layer found'
                        : `${imported.layers.length} layers found`}
                    </p>
                    {imported.layers.length > 1 &&
                      (() => {
                        // "All" means all the ones that can actually be added.
                        // A geodatabase routinely carries attribute tables and
                        // oversized feature classes alongside the usable ones.
                        const usable = imported.layers.flatMap((layer, index) =>
                          layer.unavailable ? [] : [index],
                        );
                        const allPicked = usable.length > 0 && selected.size === usable.length;
                        return (
                          <button
                            type="button"
                            onClick={() => setSelected(allPicked ? new Set() : new Set(usable))}
                            className="text-[11px] font-semibold text-blue-600 hover:text-blue-800"
                          >
                            {allPicked ? 'Clear all' : `Select all (${usable.length})`}
                          </button>
                        );
                      })()}
                  </div>

                  {imported.notices.map((notice) => (
                    <p key={notice} className="text-[11px] text-amber-700">• {notice}</p>
                  ))}

                  <ul className="max-h-64 space-y-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2">
                    {imported.layers.map((layer, index) => (
                      <li key={`${layer.name}-${index}`}>
                        <label
                          className={`flex items-start gap-2.5 rounded-lg px-2 py-2 ${
                            layer.unavailable
                              ? 'cursor-not-allowed opacity-60'
                              : 'cursor-pointer hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(index)}
                            disabled={Boolean(layer.unavailable)}
                            onChange={(event) => {
                              const next = new Set(selected);
                              if (event.target.checked) next.add(index);
                              else next.delete(index);
                              setSelected(next);
                            }}
                            className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-blue-600"
                          />
                          {/* A raster has no fill colour to swatch, so it gets
                              an icon instead — everything else keeps the
                              colour taken from the source style. */}
                          {layer.raster ? (
                            <ImageIcon
                              aria-hidden="true"
                              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400"
                            />
                          ) : (
                            <span
                              aria-hidden="true"
                              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 rounded border border-slate-300"
                              style={{ backgroundColor: layer.renderer?.color ?? '#CBD5E1' }}
                            />
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-[13px] font-medium text-slate-800">
                              {layer.name}
                            </span>
                            <span className="block text-[11px] text-slate-400">
                              {layer.raster ? (
                                <>
                                  Raster · {layer.raster.width.toLocaleString()}×
                                  {layer.raster.height.toLocaleString()} px ·{' '}
                                  {layer.raster.bandCount} band
                                  {layer.raster.bandCount === 1 ? '' : 's'}
                                </>
                              ) : (
                                <>
                                  {GEOMETRY_LABEL[layer.geometryType]} ·{' '}
                                  {layer.featureCount.toLocaleString()} feature
                                  {layer.featureCount === 1 ? '' : 's'} · {layer.fields.length} field
                                  {layer.fields.length === 1 ? '' : 's'}
                                  {layer.styleCount && layer.styleCount > 1
                                    ? ` · ${layer.styleCount} styles`
                                    : ''}
                                </>
                              )}
                            </span>
                            {layer.unavailable && (
                              <span className="block text-[11px] font-medium text-slate-500">
                                {layer.unavailable}
                              </span>
                            )}
                            {layer.notices.map((notice) => (
                              <span key={notice} className="block text-[11px] text-amber-700">
                                {notice}
                              </span>
                            ))}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={selected.size === 0 || adding !== null}
                      onClick={() => void addSelected()}
                      className="gl-btn gl-btn--primary px-3 py-1.5 text-xs"
                    >
                      {adding ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Check className="h-3.5 w-3.5" />
                      )}
                      {adding
                        ? /* Counted rather than a bare spinner: converting eight
                             feature classes takes long enough that "which one is
                             it on" is the question being asked. */
                          `Loading ${Math.min(adding.done + 1, adding.total)} of ${adding.total}…`
                        : selected.size <= 1
                          ? 'Add to map'
                          : `Add ${selected.size} layers`}
                    </button>
                    <button
                      type="button"
                      disabled={adding !== null}
                      onClick={() => {
                        imported.release?.();
                        reset();
                      }}
                      className="gl-btn gl-btn--secondary px-3 py-1.5 text-xs"
                    >
                      Choose another
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── Existing layer ──────────────────────────────────────── */
            <ExistingLayerPicker
              currentModuleId={currentModuleId}
              currentUserId={currentUserId}
              onAdd={(layer) => {
                onAddExistingLayer(layer);
                onClose();
              }}
            />
          )}
        </div>

        <div className="gl-footer flex flex-shrink-0 justify-end px-5 py-3">
          <button type="button" onClick={closeAndRelease} className="gl-btn gl-btn--secondary px-3.5 py-2 text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
